import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as nsCharts from '@netsapiens/netsapiens-charts-library';
import Iframe from 'react-iframe';
import nsApi from '@netsapiens/netsapiens-js/dist/api';

import { CircularProgress } from '@material-ui/core';
import { defineMessages, injectIntl } from 'react-intl';
import * as actions from '../../../../state';
import { dateToStr, getChartDataById } from '../../../../utils';
import BroNoIco from '../notification.png';

import styles from '../styles.module.css';

const messages = defineMessages({
  ABOVE: {
    id: 'ABOVE',
    defaultMessage: 'Above',
  },
  BELOW: {
    id: 'BELOW',
    defaultMessage: 'Below',
  },
});

const setRollingRange = (requestParams) => {
  const params = _.cloneDeep(requestParams);

  const [timeRange, timeUnit] = !params.time_range.range
    ? params.time_range.split('_')
    : params.time_range.range.split('_');

  const rangeStart = new Date(); // today
  params.end_date = dateToStr(rangeStart);

  const rangeEnd = new Date(params.end_date);
  switch (timeUnit) {
    case 'hour':
      rangeEnd.setHours(rangeStart.getHours() - timeRange);
      break;
    case 'day':
      rangeEnd.setDate(rangeStart.getDate() - timeRange);
      break;
    case 'week':
      rangeEnd.setDate(rangeStart.getDate() - (timeRange * 7));
      break;
    case 'month':
      rangeEnd.setMonth(rangeStart.getMonth() - timeRange);
      break;
    case 'year':
      rangeEnd.setYear(rangeStart.getFullYear() - timeRange);
      break;
    default:
  }
  params.start_date = dateToStr(rangeEnd);

  return params;
};

const setDailyRange = (requestParams) => {
  const params = _.cloneDeep(requestParams);

  const { daytimes } = params.time_range;

  const now = new Date();
  const thisDay = now.getDay();

  /* grab the config for current day */
  const thisDaytimeConfig = daytimes.find((daytime) => thisDay === daytime.day);

  /* grab the config for nearest active previous day */
  let prevDaytimeConfig;
  for (let i = 1; i < 7; i += 1) {
    const dayToCheck = thisDay - i > 0
      ? thisDay - i // step through days backwards
      : thisDay - i + 6; // wrap day through 0-6

    /* step through day times and check for active = true on this dayToCheck */
    prevDaytimeConfig = daytimes.find((daytime) => dayToCheck === daytime.day && daytime.active);
    if (prevDaytimeConfig) {
      break; // these were the droids we were looking for
    }
  }

  const currentHourActiveToday = now.getHours() <= thisDaytimeConfig.end_time.h
    && now.getHours() >= thisDaytimeConfig.start_time.h;

  const currentMinsActiveToday = true; // TODO: Allan please add details

  // if now is in an active window, use thisDaytimeConfig
  if (thisDaytimeConfig.active && currentHourActiveToday && currentMinsActiveToday) {
    now.setHours(thisDaytimeConfig.end_time.h, thisDaytimeConfig.end_time.m);
    params.end_date = dateToStr(now);

    now.setHours(thisDaytimeConfig.start_time.h, thisDaytimeConfig.start_time.m);
    params.start_date = dateToStr(now);
  } else { // otherwise, use prevDaytimeConfig
    const currentDay = now.getDay();

    let distance = currentDay - prevDaytimeConfig.day; // distance between now and nearest active window
    if (distance <= 0) distance += 7; // offset the day by distance, only interested in the past
    now.setDate(now.getDate() - distance);

    now.setHours(prevDaytimeConfig.end_time.h, prevDaytimeConfig.end_time.m);
    params.end_date = dateToStr(now);

    now.setHours(prevDaytimeConfig.start_time.h, prevDaytimeConfig.start_time.m);
    params.start_date = dateToStr(now);
  }

  return params;
};

const setTimeRangeParams = (requestParams) => {
  const params = _.cloneDeep(requestParams);

  // eslint-disable-next-line camelcase
  const { time_range: timeRange } = params;

  if (!timeRange.type || timeRange.type === 'rolling') {
    return setRollingRange(params);
  }

  if (timeRange.type === 'daily') {
    return setDailyRange(params);
  }

  if (timeRange.type === 'static') {
    params.start_date = timeRange.start_date;
    params.end_date = timeRange.end_date;
    return params;
  }

  return undefined;
};

function ChartCardContent({ chart, intl }) {
  const dispatch = useDispatch();

  const chartsData = useSelector(actions.readStateChartsData);
  const apiHash = useSelector((state) => actions.selectConfig(state, 'apiHash'));
  const apiURL = useSelector((state) => actions.selectConfig(state, 'apiURL'));
  const board = useSelector(actions.readStateBoard);
  const chartsManifest = useSelector(actions.readChartsManifest);
  const isNetworkConnected = useSelector(actions.selectNetworkConnected);
  const isSocketConnected = useSelector(actions.selectIsConnected);
  const socket = useSelector(actions.selectSocket);
  const user = useSelector(actions.readStateUser);
  const PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST = useSelector((state) => state.configs.PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST);
  const PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS = useSelector(
    (state) => state.configs.PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS,
  );
  const [bearerToken, setBearerToken] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await nsApi.getToken();
        setBearerToken(token);
      } catch (error) {
        console.error('Failed to fetch auth token:', error);
      }
    };

    fetchToken();
  }, []);

  const forceDeptFilter = (object, type) => {
    if (object === 'callqueue') {
      return false;
    }

    return !(object === 'callqueuereport' && (type === 'queue_name' && type === 'dnis'));
  };

  const forceFilters = (filters = [], object, type) => {
    let hasQueueFilter = false;
    let hasAgentFilter = false;

    if (filters == null) {
      // eslint-disable-next-line no-console
      console.log('filters for chart card is null');
      // eslint-disable-next-line no-console
      console.log(`object is ${object}`);
      // eslint-disable-next-line no-console
      console.log(`type is ${type}`);
      // eslint-disable-next-line no-param-reassign
      filters = [];
    }

    filters.forEach((filter) => {
      switch (filter.type) {
        case 'site':
        case 'department':
        case 'subscriber':
          hasAgentFilter = true;
          break;
        case 'callqueue':
          hasQueueFilter = true;
          break;
        default:
      }
    });

    if (PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST && !hasQueueFilter) {
      const queueList = PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST.split(',');
      filters.push({ type: 'callqueue', value: '*' });
      queueList.forEach((queue) => {
        filters.push({ type: 'callqueue', value: queue });
      });
    }

    if (forceDeptFilter(object, type) && PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS && !hasAgentFilter) {
      const deptList = PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS.split(',');
      deptList.forEach((dept) => {
        filters.push({ type: 'department', value: dept });
      });
    }

    return filters;
  };

  const filtersToAdapterParams = (initialFilters, object = 'callqueue', type = null, includeSocketParams = true) => {
    let filters = initialFilters;

    const returnParams = [];

    const siteList = [];
    const queueList = [];
    const departmentList = [];
    const subscriberList = [];

    returnParams.socket_filter = {};

    filters = forceFilters(filters, object, type);

    filters.forEach((filter) => {
      switch (filter.type) {
        case 'site':
          siteList.push(filter.value);
          break;
        case 'department':
          departmentList.push(filter.value);
          break;
        case 'callqueue':
          queueList.push(filter.value);
          if (includeSocketParams && object === 'callqueue') { returnParams.socket_filter.filter = filter.value; }
          break;
        case 'subscriber':
          subscriberList.push(filter.value);
          break;
        case 'my_subscriber':
          subscriberList.push(`${user.userId}@${user.domain}`);
          break;
        default:
      }
    });

    if (siteList.length) { returnParams.site_list = siteList.join(','); }

    if (departmentList.length) { returnParams.group_list = departmentList.join(','); }

    if (queueList.length) {
      returnParams.queue_list = queueList.join(',');
      if (includeSocketParams
        && object === 'callqueue'
        && queueList.length > 1
      ) {
        returnParams.socket_filter.filter = queueList;
      }
    }

    if (subscriberList.length) { returnParams.agent_list = subscriberList.join(','); }

    return returnParams;
  };

  const getCellComponents = (column) => {
    const source = chart.data_sources[0];
    const sourceAlerts = source.alerts;

    const boardBroNoEnabled = typeof board.properties.bro_no_enabled === 'undefined'
      || (board.properties && board.properties.bro_no_enabled);

    if (sourceAlerts && sourceAlerts.length) {
      const columnAlerts = sourceAlerts.filter((sAlert) => sAlert.column === column); // filter out alerts for other cols

      if (columnAlerts && columnAlerts.length) {
        return {
          cellComponent: nsCharts.cellComponents.SimpleThreshold,
          threshold: new nsCharts.Threshold({
            defaultColor: '#ffffff',
            direction: columnAlerts[0].direction,
            rules: columnAlerts.map((cAlert) => new nsCharts.Rule({
              color: cAlert.color,
              value: cAlert.threshold,
              ...(!cAlert.bronoEnabled || !boardBroNoEnabled ? {} : {
                alert: () => {
                  const chartDef = chartsManifest[chart.type];
                  const statDef = chartDef.dataOptions[source.stat];
                  const columnDef = statDef.supportedColumns.find((c) => cAlert.column === c.stat);
                  const alertDirectionStr = intl.formatMessage({ ...messages[cAlert.direction.toUpperCase()] });

                  // eslint-disable-next-line no-new
                  new Notification(statDef.labelStr, {
                    body: `${columnDef.labelStr} ${alertDirectionStr} ${cAlert.threshold}`,
                    icon: BroNoIco,
                  });
                },
              }),
            })),
          }),
        };
      }
    }

    return {}; // no matching alerts were found
  };

  const broNoFun = (chartParam, source, alert) => {
    const boardBroNoEnabled = typeof board.properties.bro_no_enabled === 'undefined'
      || (board.properties && board.properties.bro_no_enabled);

    if (alert.bronoEnabled && boardBroNoEnabled) {
      const chartDef = chartsManifest[chartParam.type];
      const statDef = chartDef.dataOptions[source.stat];
      const alertDirectionStr = intl.formatMessage({ ...messages[alert.direction.toUpperCase()] });
      const statDefLabelStr = _.cloneDeep(statDef.labelStr);

      let notificationContent;
      if (statDef.unit === 'minutes') {
        const minutes = Math.floor(alert.threshold / 60);
        let seconds = alert.threshold % 60;
        if (seconds < 10) { seconds = `0${seconds}`; }
        notificationContent = `${statDefLabelStr} ${alertDirectionStr} ${minutes}:${seconds}`;
      } else {
        notificationContent = `${statDefLabelStr} ${alertDirectionStr} ${alert.threshold}`;
      }

      // eslint-disable-next-line no-new
      new Notification(board.name, {
        body: notificationContent,
        icon: BroNoIco,
      });
    }
  };

  const getThresholds = (source) => {
    const defaultDefaultColor = chart.type === 'ResponsiveGauge' ? 'green' : '#E0E0E0';

    const rules = source.alerts.map((rule) => new nsCharts.Rule({
      color: rule.color,
      value: rule.threshold,
      alert: () => broNoFun(chart, source, rule),
    }));

    return new nsCharts.Threshold({
      direction: source.alerts[0].direction,
      defaultColor: chart.properties.defaultColor || defaultDefaultColor,
      rules,
    });
  };

  const alertParams = (source) => {
    const params = {};

    switch (chart.type) {
      case 'ResponsiveStat':
        params.threshold = getThresholds(source);
        break;
      case 'ResponsiveGauge':
        params.threshold = getThresholds(source);
        params.outOf = source.queryParams.outOf;
        break;
      default:
    }

    return params;
  };

  const generateApiConnector = (source) => {
    const statDef = chartsManifest[chart.type].dataOptions[source.stat];

    if (!statDef) {
      console.error(`Failed to resolve "${source.stat}" data definitions from "${chart.type}" source manifest.`);
    }

    let adapterName;
    let requestParams = {
      ...statDef.apiParams, // base api parameters
      ...source.queryParams, // user-specified query parameters, should
    };

    /* if dataSource has subcategories */
    if (statDef.hasCategories) {
      const categoryDef = statDef.categoryDefs.find((cd) => cd.category === source.category);
      adapterName = categoryDef.adapter; // adapter will depend on the category

      requestParams = {
        ...requestParams,
        ...categoryDef.apiParams, // specific api parameters for category
      };
    }

    /* if dataSource has columns */
    if (statDef.hasColumns) {
      adapterName = statDef.adapter;
    }

    /* no adapter name was found yet, so use the base stat definition */
    if (!adapterName && statDef.adapter) { adapterName = statDef.adapter; }

    /* append chart title for bookkeeping */
    requestParams.chart_title = chart.name;

    // TODO TODO TODO
    // only send user if not stats as its messing up agent reports
    if (requestParams.object !== 'statistics') { requestParams.user = user ? user.userId : board.owner_id; }
    requestParams.domain = user ? user.domain : board.domain;

    /* convert time_range to specific start_date / end_date */
    if (requestParams.time_range) {
      requestParams = setTimeRangeParams(requestParams);
    }

    /* add filters */
    const hasForcedFilters = PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST
      || PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS;

    if ((source.filters && source.filters.length) || hasForcedFilters) {
      const filterParams = filtersToAdapterParams(
        source.filters,
        requestParams.object,
        requestParams.type || null,
        false,
      );

      requestParams = {
        ...requestParams,
        ...filterParams,
      };
    }

    /* build adapter parameters */
    let adapterParams = {
      ...statDef.adapterParams,
      dateTimeRange: {
        startDateTime: requestParams.start_date,
        endDateTime: requestParams.end_date,
      },
    };

    // add format
    if (source.formatType || statDef.defaultFormatType) {
      adapterParams.formatType = source.formatType || statDef.defaultFormatType;
    }

    // add format precision (decimal) and other properties
    if (chart.properties) {
      if (!_.isUndefined(chart.properties.decimalPlaces)) {
        adapterParams.formatPrecision = chart.properties.decimalPlaces;
      }

      adapterParams.paging = chart.properties.overflow !== 'scroll';

      adapterParams.styleOverrides = {};
      if (!_.isUndefined(chart.properties.cellPadding)) {
        adapterParams.styleOverrides.cellPadding = chart.properties.cellPadding;
      }
      if (!_.isUndefined(chart.properties.headerFontSize)) {
        adapterParams.styleOverrides.headerFontSize = chart.properties.headerFontSize;
      }
      if (!_.isUndefined(chart.properties.bodyFontSize)) {
        adapterParams.styleOverrides.bodyFontSize = chart.properties.bodyFontSize;
      }
    }

    if (requestParams.stat) {
      adapterParams.stat = requestParams.stat;
    }

    /* set period, derive interval */
    if (requestParams.period) {
      adapterParams.period = requestParams.period;
    }

    /* build the column components if the request includes columns */
    if (requestParams.stats) {
      let columnCollection;
      if (source.stat === 'Agent') { columnCollection = 'agentStatusColumns'; }
      if (source.stat === 'Callqueue') { columnCollection = 'callQueuesColumns'; }
      if (source.stat === 'Queued') { columnCollection = 'callsWaitingColumns'; }

      const baseColumns = statDef.baseColumns.map((column) => {
        try {
          return new nsCharts[columnCollection][column](getCellComponents(column));
        } catch (e) {
          console.error(`Cannot render column ${column} || ${e}`);
          return undefined;
        }
      });

      requestParams.stats = _.filter(requestParams.stats, (stat) => stat !== 'gau_session');

      const selectedColumns = requestParams.stats.map((column) => {
        try {
          // todo here
          return new nsCharts[columnCollection][column](getCellComponents(column));
        } catch (e) {
          console.error(`Cannot render column ${column} || ${e}`);
          return undefined;
        }
      });

      if (columnCollection === 'callQueuesColumns') {
        requestParams.stats.push('gau_session');
      }

      adapterParams.columns = [
        ...baseColumns,
        ...selectedColumns,
      ];
    }

    /* add alerts as adapterParams */
    if (source.alerts && source.alerts.length) {
      adapterParams = {
        ...adapterParams,
        ...alertParams(source),
      };
    } else if (chart.type === 'ResponsiveStat') { // no alerts set, but the default still needs to be added
      adapterParams = {
        ...adapterParams,
        threshold: new nsCharts.Threshold({
          defaultColor: chart.properties.defaultColor || '#E0E0E0',
        }),
      };
    } else if (chart.type === 'ResponsiveGauge') { // no alerts set, but the default still needs to be added
      adapterParams = {
        ...adapterParams,
        threshold: new nsCharts.Threshold({
          defaultColor: chart.properties.defaultColor || 'green',
        }),
      };

      if (source.queryParams.outOf) {
        adapterParams.outOf = source.queryParams.outOf;
      }
    }

    return {
      params: requestParams,
      adapter: nsCharts[adapterName],
      adapterParams,
    };
  };

  const generateSocketConnector = (source) => {
    let socketEvents;
    let socketSubscriptions;
    let socketFilter = {};

    if (source.stat === 'Agent' && source.socketEvents) {
      const hasPresenceEvent = source.socketEvents.some((event) => event.name === 'presence');
      if (!hasPresenceEvent) {
        const adapterName = source.socketEvents[0]?.adapter 
                           || (source.socketEvents[0]?.name === 'agent' ? 'agentsStatusSocketToTableAdapter' 
                             : 'agentsStatusSocketToGridAdapter');
        source.socketEvents.push({
          name: 'presence',
          adapter: adapterName,
        });
        if (source.socketSubscriptions) {
          const template = { ...source.socketSubscriptions[0] };
          source.socketSubscriptions.push({
            ...template,
            type: 'presence',
          });
        }
      }
    }
  
    if (source.socketEvents) {
      const statDef = chartsManifest[chart.type].dataOptions[source.stat];

      socketEvents = source.socketEvents.map((event) => {
        let adapterParams = event.adapterParams || {};

        if (statDef.hasColumns) {
          const allColumns = {
            ...nsCharts.agentStatusColumns,
            ...nsCharts.callQueuesColumns,
            ...nsCharts.callsWaitingColumns,
          };

          // eslint-disable-next-line max-len
          const baseColumns = statDef.baseColumns.map((columnName) => new allColumns[columnName](getCellComponents(columnName)));
          // eslint-disable-next-line max-len
          const selectedColumns = source.queryParams.stats.map((columnName) => new allColumns[columnName](getCellComponents(columnName)));

          adapterParams.columns = [...baseColumns, ...selectedColumns];
        }

        const hasForcedFilters = (PORTAL_CALL_QUEUE_MANAGER_VIEW_LIST || PORTAL_CALL_QUEUE_MANAGER_AGENT_GROUPS);

        if ((source.filters && source.filters.length) || hasForcedFilters) {
          const filterParams = filtersToAdapterParams(source.filters, adapterParams.object, adapterParams.type, true);

          if (filterParams.socket_filter && source.stat !== 'Agent') { socketFilter = filterParams.socket_filter; }

          adapterParams = {
            ...adapterParams,
            ...filterParams,
          };
        }

        // add format
        if (source.formatType || statDef.defaultFormatType) {
          adapterParams.formatType = source.formatType || statDef.defaultFormatType;
        }

        // add format precision (decimal) and other properties
        if (chart.properties) {
          if (!_.isUndefined(chart.properties.decimalPlaces)) {
            adapterParams.formatPrecision = chart.properties.decimalPlaces;
          }

          adapterParams.paging = chart.properties.overflow !== 'scroll';

          adapterParams.styleOverrides = {};
          if (!_.isUndefined(chart.properties.cellPadding)) {
            adapterParams.styleOverrides.cellPadding = chart.properties.cellPadding;
          }
          if (!_.isUndefined(chart.properties.headerFontSize)) {
            adapterParams.styleOverrides.headerFontSize = chart.properties.headerFontSize;
          }
          if (!_.isUndefined(chart.properties.bodyFontSize)) {
            adapterParams.styleOverrides.bodyFontSize = chart.properties.bodyFontSize;
          }
        }

        /* add alerts as adapterParams */
        if (!statDef.hasColumns && source.alerts && source.alerts.length) {
          adapterParams = {
            ...adapterParams,
            ...alertParams(source),
          };
        } else if (chart.type === 'ResponsiveStat') { // no alerts set, but the default still needs to be added
          adapterParams = {
            ...adapterParams,
            threshold: new nsCharts.Threshold({
              defaultColor: chart.properties.defaultColor || '#E0E0E0',
            }),
          };

          if (source.queryParams.outOf) adapterParams.outOf = source.queryParams.outOf;
        } else if (chart.type === 'ResponsiveGauge') { // no alerts set, but the default still needs to be added
          adapterParams = {
            ...adapterParams,
            threshold: new nsCharts.Threshold({
              defaultColor: chart.properties.defaultColor || 'green',
            }),
          };

          if (source.queryParams.outOf) adapterParams.outOf = source.queryParams.outOf;
        }

        return {
          ...event,
          adapter: nsCharts[event.adapter],
          adapterParams,
        };
      });
    }

    let token = bearerToken;
    if (chart.data_token && chart.data_token.access_token) {
      token = chart.data_token.access_token;
    }

    const userX = user ? user.userId : null;

    if (source.socketSubscriptions) {
      socketSubscriptions = [];

      for (let i = 0; i < source.socketSubscriptions.length; i += 1) {
        const sub = source.socketSubscriptions[i];
        sub.user = userX;
        sub.bearer = token;

        if (typeof socketFilter.filter === 'string') {
          sub.filter = socketFilter;
          socketSubscriptions.push(sub);
        } else if (typeof socketFilter.filter === 'object') {
          for (let j = 0; j < socketFilter.filter.length; j += 1) {
            sub.filter = socketFilter.filter[j];
            socketSubscriptions.push(_.cloneDeep(sub));
          }
        } else {
          socketSubscriptions.push(sub);
        }
      }
    }

    // send to redux for socket reconnects
    for (let i = 0; i < socketSubscriptions.length; i += 1) {
      const formatted = _.cloneDeep(socketSubscriptions[i]);
      formatted.chartId = chart.id;

      let { domain } = board;
      if (user) {
        domain = user.domain;
      }

      formatted.domain = domain;

      delete formatted.bearer;
      dispatch(actions.setSubscription(formatted));
      socketSubscriptions[i].domain = domain;
    }

    return {
      socketEvents,
      socketSubscriptions,
    };
  };

  // if (!chart.type) { return cardPlaceholder('new'); }

  const chartData = getChartDataById(chart.id, chartsData);

  let contentComponent;
  const chartProps = chart.properties;

  const authToken = chart.data_token || null;

  let ChartComponent;
  let chartAlerts = [];
  let finalProps;

  switch (chart.type) {
    case 'ResponsiveNote':
      chartProps.overflowX = 'hidden';
      chartProps.overflowY = (chartProps.enableScrolling) ? 'scroll' : 'hidden';

      contentComponent = (
        <div className={styles.noteContent} style={{ ...chartProps }}>
          {chart.data_sources[0].note_text}
        </div>
      );
      break;
    case 'ResponsiveIFrame':
      contentComponent = (
        <div style={{ width: '100%', height: '100%' }}>
          <Iframe
            url={chart.data_sources[0].queryParams.url}
            className={styles.frameContent}
            width="100%"
            height="100%"
            display="initial"
            position="relative"
            {...chartProps}
          />
        </div>
      );
      break;
    default:
      ChartComponent = nsCharts[chart.type];

      // if this is a table with a custom data source, the chart type will need to be switched
      if (chart.type === 'ResponsiveTable'
        && chart.data_sources
        && chart.data_sources[0].stat === 'custom'
      ) {
        ChartComponent = nsCharts.ResponsiveCustomTable;
      }

      // if the tickFormat prop is set, translate it to the appropriate logic
      if (chartProps && chartProps.axisBottom) {
        chartProps.axisBottom.legend = null;

        /* if (chartProps.axisBottom.tickFormat) {
          chartProps.axisBottom.format = x => this.axisBottomFormatFunc(x, chartProps);
        } */
      }

      // if the enableAxisLabelY prop is set, set axisLeft
      if (chartProps && chartProps.enableAxisLabelY) {
        const labels = [];

        chart.data_sources.forEach((source) => {
          labels.push(chartsManifest[chart.type].dataOptions[source.stat].unit);
        });

        chartProps.axisLeft = {
          legend: labels[0],
          legendOffset: -33,
          orient: 'left',
          legendPosition: 'middle',
        };
      } else { // clear axisLeft from props if enableAxisLabelY isn't set
        chartProps.axisLeft = {};
      }

      // board is always interactive unless chart is gauge, but will be obscured by overlays in some contexts
      chartProps.isInteractive = chart.type !== 'ResponsiveGauge';

      // force disable animated until performance issues can be resolved
      chartProps.animate = false;

      /* add alerts as chart props */
      chart.data_sources.forEach((source) => {
        if (source.alerts && source.alerts.length) {
          chartAlerts = chartAlerts.concat(source.alerts);
        }
      });

      if (chartAlerts.length) {
        switch (chart.type) {
          case 'ResponsiveLine':
            // chartProps.dotColor = (dot) => {
            //   // alert dot coloring logic  goes here
            //   dot.color;
            // }
            break;
          default:
        }
      }

      // trim out meta stuff for final consumption, finalProps won't map back to the initial props structure
      finalProps = _.cloneDeep(chartProps);
      if (finalProps.axisBottom) {
        delete (finalProps.axisBottom.tickFormat);
      }

      if (chart.type === 'ResponsiveLine' && (finalProps.enableLegend === undefined || finalProps.enableLegend)) {
        finalProps.legends = [{
          anchor: 'top-right',
          direction: 'column',
          justify: false,
          translateX: 85,
          translateY: 5,
          itemDirection: 'left-to-right',
          itemsSpacing: 10,
          itemWidth: 72,
          itemHeight: 12,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
        }];

        if (!finalProps.margin) finalProps.margin = {};

        finalProps.margin.right += (finalProps.legendWidth || 100);
      }

      // instantiate the chart component
      contentComponent = (
        <ChartComponent
          className="chart-card"
          tooltip={nsCharts.customTooltip}
          {...finalProps}
          {...chartData}
        />
      );
      break;
  }

  const apiConnections = [];
  const socketConnections = [];
  let socketEvents = [];
  let socketSubscriptions = [];
  const urlConnections = [];
  let interval = null;
  let rolling = false;

  chart.data_sources.forEach((source) => {
    let sc;
    let adapterParams;
    let defaultDefaultColor;

    switch (source.type) {
      case 'api':
        if (source.socketSubscriptions) {
          sc = generateSocketConnector(source);
          socketEvents.push(sc.socketEvents);
          socketSubscriptions.push(sc.socketSubscriptions);
        }

        if (source.queryParams && source.queryParams.time_range) {
          rolling = !source.queryParams.time_range.type || source.queryParams.time_range.type === 'rolling';
          interval = 60; // default interval

          const statDef = chartsManifest[chart.type].dataOptions[source.stat];
          if (statDef && statDef.period) {
            interval = source.queryParams.period === 'dayAndHourAndMinute'
              ? 60 // 1 min
              : 60 * 60; // sec * min = 1 hour
          }
        }

        try {
          apiConnections.push(generateApiConnector(source));
        } catch (e) {
          console.error(`Failed to generate API connection for ${chart.name} || ${e}`);
        }
        break;
      case 'socket':
        sc = generateSocketConnector(source);
        socketEvents.push(sc.socketEvents);
        socketSubscriptions.push(sc.socketSubscriptions);
        socketConnections.push(sc);
        break;
      case 'url': // custom source
        switch (chart.type) {
          case 'ResponsiveIFrame':
            return; // skip ResponsiveIFrame
          case 'ResponsiveGauge':
          case 'ResponsiveStat':
            adapterParams = {};
            defaultDefaultColor = chart.type === 'ResponsiveGauge' ? 'green' : '#E0E0E0';

            if (source.queryParams.outOf) { adapterParams.outOf = source.queryParams.outOf; }

            /* add alerts as adapterParams */
            if (source.alerts && source.alerts.length) {
              adapterParams = {
                ...adapterParams,
                ...alertParams(source),
              };
            } else if (['ResponsiveStat', 'ResponsiveGauge'].includes(chart.type)) { // no alerts set, but the default still needs to be added
              adapterParams = {
                ...adapterParams,
                threshold: new nsCharts.Threshold({
                  defaultColor: chart.properties.defaultColor || defaultDefaultColor,
                }),
              };
            }

            // eslint-disable-next-line no-param-reassign
            chart.invalidUrl = !source.queryParams.url; // missing url

            if (!chart.invalidUrl) {
              urlConnections.push({
                url: source.queryParams.url,
                adapter: nsCharts.customToSDPAdapter,
                adapterParams,
              });
            }
            break;
          case 'ResponsiveTable':
            if (!chart.invalidUrl) {
              urlConnections.push({
                url: source.queryParams.url,
              });
            }
            break;
          default:
        }
        break;
      default:
    }
  });

  socketEvents = _.uniq(_.flatten(socketEvents));
  socketSubscriptions = _.uniq(_.flatten(socketSubscriptions));

  const animOffset = _.random(0, 2000);

  const loaderEl = (
    <div className={styles.loaderContainer}>
      <div className={styles.loaderFloater}>
        <CircularProgress
          className={`mui-loader ${styles.loadingIcon}`}
          style={{ animationDelay: `${animOffset}ms` }}
        />
      </div>
    </div>
  );

  if (socketSubscriptions.length) {
    socketSubscriptions.forEach((sub) => {
      if (sub) {
        // eslint-disable-next-line
        sub.application = 'analytics';
        if (socket) { socket.emit('subscribe', sub); }
      }
    });
  }

  if (apiConnections.length) {
    if (socketEvents.length) {
      contentComponent = (
        <nsCharts.ApiConnector
          url={apiURL}
          requests={apiConnections}
          apiHash={apiHash}
          authToken={authToken}
          jwtToken={nsApi.getToken}
          loader={loaderEl}
          networkConnected={isNetworkConnected}
          socketConnected={isSocketConnected}
          interval={interval}
          rolling={rolling}
        >
          <nsCharts.SocketConnector
            events={socketEvents}
            socket={socket}
            socketConnected={isSocketConnected}
          >
            {contentComponent}
          </nsCharts.SocketConnector>
        </nsCharts.ApiConnector>
      );
    } else {
      contentComponent = (
        <nsCharts.ApiConnector
          url={apiURL}
          requests={apiConnections}
          apiHash={apiHash}
          authToken={authToken}
          jwtToken={nsApi.getToken}
          loader={loaderEl}
          networkConnected={isNetworkConnected}
          socketConnected={isSocketConnected}
          interval={interval}
          rolling={rolling}
        >
          {contentComponent}
        </nsCharts.ApiConnector>
      );
    }
  }

  if (urlConnections.length) {
    contentComponent = (
      <nsCharts.UrlConnector
        url={urlConnections[0].url}
        adapter={urlConnections[0].adapter}
        adapterParams={urlConnections[0].adapterParams}
        loader={loaderEl}
      >
        {contentComponent}
      </nsCharts.UrlConnector>
    );
  }

  if (socketConnections.length) {
    contentComponent = (
      <nsCharts.SocketConnector
        events={socketEvents}
        loader={loaderEl}
        socket={socket}
        socketConnected={isSocketConnected}
      >
        {contentComponent}
      </nsCharts.SocketConnector>
    );
  }

  return contentComponent;
}

export default injectIntl(ChartCardContent);
